import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { Paper, Typography, Chip } from '@mui/material';
import { Person as HumanIcon, Schedule as DeadlineIcon } from '@mui/icons-material';
import { WorkflowNode } from '../../../../types/workflow.types';

const HumanTaskNode: React.FC<NodeProps<WorkflowNode['data']>> = ({ data, selected }) => {
  const config = data?.config || {};

  const getPriorityColor = (priority?: string) => {
    switch (priority) {
      case 'urgent': return '#F44336';
      case 'high': return '#FF9800';
      case 'medium': return '#FFC107';
      case 'low': return '#4CAF50';
      default: return '#FFC107';
    }
  };

  return (
    <Paper
      elevation={selected ? 8 : 2}
      style={{
        padding: 16,
        minWidth: 150,
        maxWidth: 250,
        backgroundColor: '#FF9800',
        color: 'white',
        border: selected ? '3px solid #1976d2' : '1px solid #F57C00',
        borderRadius: 8,
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
      }}
    >
      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Left}
        style={{
          backgroundColor: '#F57C00',
          border: '2px solid white',
          width: 12,
          height: 12,
        }}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <HumanIcon style={{ fontSize: 20 }} />
        <Typography variant="subtitle2" fontWeight="bold">
          {data?.title || 'Human Task'}
        </Typography>
      </div>

      {data?.description && (
        <Typography variant="caption" style={{ opacity: 0.9, fontSize: '0.7rem', marginBottom: 8, display: 'block' }}>
          {data?.description}
        </Typography>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
        {config?.priority && (
          <Chip
            label={config?.priority?.toUpperCase() || ''}
            size="small"
            style={{
              backgroundColor: getPriorityColor(config?.priority),
              color: 'white',
              fontSize: '0.6rem',
              height: 16,
            }}
          />
        )}

        {config?.assigneeRole && (
          <Chip
            label={config?.assigneeRole || ''}
            size="small"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              color: 'white',
              fontSize: '0.6rem',
              height: 16,
            }}
          />
        )}
      </div>

      {config?.deadline && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
          <DeadlineIcon style={{ fontSize: 12 }} />
          <Typography variant="caption" style={{ opacity: 0.8, fontSize: '0.65rem' }}>
            Due: {config?.deadline ? new Date(config.deadline).toLocaleDateString() : ''}
          </Typography>
        </div>
      )}

      {config?.formFields && config.formFields.length > 0 && (
        <Typography variant="caption" style={{ opacity: 0.8, fontSize: '0.65rem' }}>
          {config?.formFields?.length || 0} form field{(config?.formFields?.length || 0) > 1 ? 's' : ''}
        </Typography>
      )}

      {!config?.assigneeId && !config?.assigneeRole && (
        <Typography variant="caption" style={{ opacity: 0.7, fontSize: '0.65rem' }}>
          Click to configure assignment
        </Typography>
      )}

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Right}
        style={{
          backgroundColor: '#F57C00',
          border: '2px solid white',
          width: 12,
          height: 12,
        }}
      />
    </Paper>
  );
};

export default HumanTaskNode;
